// web/src/console/sections/ProductFeedbackSection.tsx
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { consoleApi, type ConsoleProductFeedback } from "../../lib/consoleApi";
import { authHeaders } from "../../lib/auth";
import { useConsole } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { StatCard } from "../charts";
import { CardTitle } from "../Term";
import { fmtRelative } from "../format";

/**
 * Product feedback review console (openspec/feedback-redesign-unified.md §5.3,
 * plan Task C5). The `product_feedback` DB table (C1/C3) is the source of truth;
 * this is its review surface: a list (newest-unresolved first) on the left and a
 * detail panel on the right with the captured context, the screenshot (lightbox),
 * the optional GitHub-mirror issue number, and the fixed triage vocabulary. A
 * `conversation_id`, when present, deep-links into the existing conversation
 * viewer. Screenshot bytes are fetched per-item from the C4 blob endpoint so the
 * list never drags blobs. Admin-only / tenant-scoped by the backend.
 */

/** Fixed triage vocabulary → backend action token + a single-key shortcut. */
const TRIAGE: { label: string; action: string; key: string }[] = [
  { label: "受理", action: "受理", key: "a" },
  { label: "重复", action: "重复", key: "d" },
  { label: "不予处理", action: "不予处理", key: "x" },
  { label: "稍后", action: "稍后", key: "l" },
  { label: "指派", action: "指派", key: "g" },
  { label: "标记已解决", action: "resolved", key: "r" }
];

const STATUS_LABEL: Record<string, string> = {
  new: "待处理",
  in_progress: "处理中",
  resolved: "已解决",
  wont_fix: "不予处理"
};

const isOpen = (status: string): boolean => status !== "resolved" && status !== "wont_fix";
const ms = (iso: string): number => new Date(iso).getTime();

export function ProductFeedbackSection(): JSX.Element {
  const { tick } = useConsole();
  const navigate = useNavigate();
  const list = useAsync(() => consoleApi.fetchProductFeedback(), [tick]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  // Newest-unresolved first: open items sink above closed ones, each group
  // ordered newest-first (the API already returns created_at DESC).
  const ordered = useMemo(
    () =>
      [...items].sort((a, b) => {
        const oa = isOpen(a.status) ? 0 : 1;
        const ob = isOpen(b.status) ? 0 : 1;
        if (oa !== ob) return oa - ob;
        return ms(b.created_at) - ms(a.created_at);
      }),
    [items]
  );
  const openCount = useMemo(() => items.filter((f) => isOpen(f.status)).length, [items]);
  const selected: ConsoleProductFeedback | null = ordered.find((f) => f.id === selectedId) ?? null;

  // Per-item screenshot: fetch the blob with the admin token and hand the detail
  // panel an object URL (an <img src> cannot carry the Authorization header).
  const shot = useAsync(async (): Promise<string | null> => {
    if (selected?.has_screenshot !== true) return null;
    const res = await fetch(`/api/admin/product-feedback/${selected.id}/screenshot`, { headers: authHeaders() });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  }, [selectedId]);

  const runTriage = async (action: string): Promise<void> => {
    if (selected === null) return;
    let assignee: string | undefined;
    if (action === "指派") {
      const who = window.prompt("指派给（用户名）");
      if (who === null || who.trim() === "") return;
      assignee = who.trim();
    }
    setBusy(true);
    try {
      await consoleApi.triageProductFeedback(selected.id, action, assignee);
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts for the triage actions while a row is open (§5.3).
  useEffect(() => {
    if (selected === null) return;
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target !== null && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      const hit = TRIAGE.find((t) => t.key === e.key.toLowerCase());
      if (hit !== undefined && !busy) {
        e.preventDefault();
        void runTriage(hit.action);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, busy]);

  if (list.loading) return <div className="cc-skeleton" style={{ height: 200 }} />;
  if (list.error) {
    return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={list.reload}>重试</button></div>;
  }

  return (
    <>
      <div className="cc-grid-stats">
        <StatCard label="反馈总数" value={String(items.length)} />
        <StatCard label="待处理" value={String(openCount)} {...(openCount > 0 ? { tone: "warn" as const } : {})} />
      </div>
      <div className="pf-layout">
        <div className="cc-card">
          <CardTitle>产品反馈</CardTitle>
          {ordered.length === 0 ? (
            <div className="cc-empty">暂无反馈</div>
          ) : (
            <table className="cc-table">
              <thead><tr><th>标题</th><th>来源</th><th>状态</th><th>时间</th></tr></thead>
              <tbody>
                {ordered.map((f) => (
                  <tr key={f.id} className={`pf-row${selectedId === f.id ? " active" : ""}`} onClick={() => setSelectedId(f.id)}>
                    <td><button className="cc-link" type="button">{f.title ?? "（无标题）"}</button></td>
                    <td>{f.username ?? "—"} <span style={{ color: "var(--text-tertiary)" }}>· {f.tenant ?? "—"}</span></td>
                    <td><span className={`cc-tag cc-tone-${isOpen(f.status) ? "warn" : "ok"}`}>{STATUS_LABEL[f.status] ?? f.status}</span></td>
                    <td>{fmtRelative(ms(f.created_at))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="cc-card">
          {selected === null ? (
            <div className="cc-empty">选择左侧一条反馈查看详情</div>
          ) : (
            <>
              <CardTitle>{selected.category ?? "反馈"}</CardTitle>
              <div className="cc-chat-title">{selected.title ?? "（无标题）"}</div>
              <p className="pf-desc">{selected.description ?? "（无描述）"}</p>
              <dl className="pf-kv">
                <dt>用户</dt><dd>{selected.username ?? "—"} · {selected.tenant ?? "—"}</dd>
                <dt>企业</dt><dd>{selected.enterprise ?? "—"}</dd>
                <dt>页面</dt><dd>{selected.page ?? "—"}</dd>
                <dt>版本</dt><dd>{selected.app_version ?? "—"}</dd>
                <dt>响应</dt><dd>{selected.response_id ?? "—"}</dd>
                <dt>提交时间</dt><dd>{fmtRelative(ms(selected.created_at))}</dd>
              </dl>
              {selected.conversation_id !== null && (
                <button
                  className="cc-link"
                  type="button"
                  onClick={() => navigate(`/admin/conversations/${selected.conversation_id}`)}
                >
                  打开完整对话
                </button>
              )}
              {selected.github_issue_number !== null && (
                <div className="pf-mirror">GitHub 镜像 #{selected.github_issue_number}</div>
              )}
              {selected.has_screenshot && shot.data !== null && (
                <img
                  className="pf-shot"
                  src={shot.data}
                  alt="反馈截图"
                  onClick={() => setLightboxOpen(true)}
                />
              )}
              <div className="pf-triage">
                {TRIAGE.map((t) => (
                  <button
                    key={t.action}
                    className="cc-range"
                    type="button"
                    disabled={busy}
                    title={`快捷键 ${t.key.toUpperCase()}`}
                    onClick={() => void runTriage(t.action)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {lightboxOpen && shot.data !== null && (
        <div className="pf-lightbox" onClick={() => setLightboxOpen(false)}>
          <img src={shot.data} alt="反馈截图（放大）" />
        </div>
      )}
    </>
  );
}
